import React, { useMemo } from "react";
import {
  Drawer,
  Box,
  Typography,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  Divider,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  ToggleButton,
  TextField,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  FormHelperText,
  Alert,
} from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import SearchIcon from "@mui/icons-material/Search";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useCrosstabConfigDrawer } from "@/hooks/use-crosstab-config-drawer";
import {
  FocusSelection,
  TimePeriod,
  StatTestType,
  SignificanceLevel,
} from "@store/slices/crosstab-config-slice";
import { QuestionVariablesResponse } from "@models/Filter";
import { filterVisiblePreservingSelected } from "@utils/chart-visibility-utils";
import HiddenInDatasetBadge from "@components/common/HiddenInDatasetBadge";
import ChartHiddenContentWarning from "@components/charts/common/ChartHiddenContentWarning";
import HPInput from "../common/Input";
import HPButton from "../common/Button";
import DrawerFilterToggles from "./common/DrawerFilterToggles";
import EmptyChartState from "../common/EmptyChartState";
import { QuestionsList } from "../modals/chart-modal/QuestionsList";
import CrosstabTable from "./CrosstabTable";
import TimePeriodSelection from "./TimePeriodSelection";
import RollingAverageSection from "./RollingAverageSection";
import CrosstabCustomizationPanel from "./CrosstabCustomizationPanel";
import ColorCodingSection from "@components/charts/color-coding/ColorCodingSection";
import { getMetricTypeFromQuestionType } from "@utils/color-coding-utils";
import PerSegmentWeightConfig from "./PerSegmentWeightConfig";
import RollupBuilder from "@components/modals/chart-modal/RollupBuilder";
import CategoryRollupsSection from "@components/charts/CategoryRollupsSection";
import CategoryRollupsNotice from "@components/charts/CategoryRollupsNotice";
import type { WidgetRollup } from "@models/Rollup";
import { getTimePeriodTitleSuffix } from "@/utils/time-period-display";
import {
  resolveTimePeriodInWindow,
  getPointInTimeUnitForPeriod,
} from "@/utils/time-period-utils";
import { getRollingAverageTitleSuffix } from "@/utils/rolling-average-utils";
import { useGetDatasetByIdQuery } from "@store/api/dataset-api";
import {
  buildDefaultDateGuardrails,
  type GuardrailRollingOption,
  type ViewerSelectableTimePeriod,
} from "@models/Dataset";
import {
  updateViewerOptions,
  crosstabConfigDispatcher,
} from "@store/slices/crosstab-config-slice";
import PointInTimeViewerOptionsSection from "./PointInTimeViewerOptionsSection";
import BaseSection from "@components/charts/common/base-config/BaseSection";
import { useAppDispatch } from "@store/index";
import { showSnackbar } from "@store/slices/snackbar-slice";

// Constants
const CONFIG_PANEL_WIDTH = 400;
const VIEWER_PIT_PERIODS = new Set([
  "latest_month",
  "previous_month",
  "latest_quarter",
  "previous_quarter",
  "latest_semester",
  "previous_semester",
]);

const CrosstabConfigDrawer: React.FC = () => {
  const {
    // State
    currentConfig,
    isDrawerOpen,
    editingWidgetId,
    weightOptions,
    rowVariables,
    columnVariables,
    rowQuestionSelection,
    columnQuestionSelection,
    activeFilters,
    localApplyFilters,
    isTogglingFilters,
    usePreviewDateFilter,
    handlePreviewDateFilterToggle,

    // Visualization Base
    visualizationBase,
    onVisualizationBaseChange,

    // Computed
    rowNeedsFocus,
    columnNeedsFocus,
    isRowFocusBlocked,
    isColumnFocusBlocked,
    isRowQuestionHidden,
    isColumnQuestionHidden,
    hiddenRowSourceIds,
    hiddenColumnSourceIds,
    hiddenRowLabels,
    hiddenColumnLabels,
    hasHiddenContent,
    canSave,
    isFetchingCrosstabData,
    crosstabPreviewData,
    columnNValues,
    hasSuppressedCells,
    columnVariableIds,
    isAllTimePeriod,

    // Handlers
    handleSave,
    handleDiscard,
    handleRowQuestionSelect,
    handleColumnQuestionSelect,
    handleFilterToggle,
    handleRowFocusTypeChange,
    handleColumnFocusTypeChange,
    handleRowVariableSelect,
    handleRowValueSelect,
    handleColumnVariableSelect,
    handleColumnValueSelect,
    handleRowFocusMetricChange,
    handleRowRollupNameChange,
    handleRowRollupLabelsChange,
    handleColumnFocusMetricChange,
    handleColumnRollupNameChange,
    handleColumnRollupLabelsChange,
    handleWeightChange,
    handleTimePeriodChange,
    handleTimePeriodDatesChange,
    handleStatTestingChange,

    // Actions
    closeChartDrawer,
    toggleRowVisibility,
    toggleColumnVisibility,
    updateCustomRowLabel,
    updateCustomColumnLabel,
    resetCustomRowLabel,
    resetCustomColumnLabel,
    showAllRows,
    hideAllRows,
    showAllColumns,
    hideAllColumns,
    reorderRows,
    reorderColumns,
    updateColorCodingTemplate,
    updateChartTitle,
    updateChartSubtitle,
    updateTitleBold,
    updateTitleUnderline,
    updatePercentageFormatting,
    updateShowN,
    updateMinimumBaseSize,
    updatePerSegmentWeights,
    updateStatTestingEnabled,
    updateStatTestType,
    updateSignificanceLevel,
    updateMinimumSampleSize,
    updateCompareToTotal,
    updateReferenceColumnEntity,
    handleReferenceColumnSelect,
    updateSortingOption,
    updateRollingAverageEnabled,
    updateRollingAverageWindowSize,
    handleRollingAverageChange,
    localMinimumBaseSize,
    setLocalMinimumBaseSize,
    addCategoryRollup,
    updateCategoryRollup,
    deleteCategoryRollup,
    handleCategoryRollupsChange,
    rowRollupOptions,
    addCrossCategoryRollup,
    updateCrossCategoryRollup,
    deleteCrossCategoryRollup,
    handleCrossCategoryRollupsChange,
    columnRollupOptions,
    columnRollupWeightUnsupported,
    columnRollupWeightDisabledMessage,
    columnRollupIneligibleSources,
    crossRollupConfigError,
    overlapTestingUnavailable,
  } = useCrosstabConfigDrawer();

  const handleCategoryRollupCreate = (rollup: WidgetRollup) => {
    addCategoryRollup(rollup);
    handleCategoryRollupsChange([...currentConfig.categoryRollups, rollup]);
  };

  const handleCategoryRollupUpdate = (rollup: WidgetRollup) => {
    updateCategoryRollup(rollup);
    handleCategoryRollupsChange(
      currentConfig.categoryRollups.map((r) => (r.id === rollup.id ? rollup : r)),
    );
  };

  const handleCategoryRollupDelete = (rollupId: string) => {
    deleteCategoryRollup(rollupId);
    handleCategoryRollupsChange(
      currentConfig.categoryRollups.filter((r) => r.id !== rollupId),
    );
  };

  const handleCrossCategoryRollupCreate = (rollup: WidgetRollup) => {
    addCrossCategoryRollup(rollup);
    handleCrossCategoryRollupsChange([
      ...currentConfig.crossCategoryRollups,
      rollup,
    ]);
  };

  const handleCrossCategoryRollupUpdate = (rollup: WidgetRollup) => {
    updateCrossCategoryRollup(rollup);
    handleCrossCategoryRollupsChange(
      currentConfig.crossCategoryRollups.map((r) =>
        r.id === rollup.id ? rollup : r,
      ),
    );
  };

  const handleCrossCategoryRollupDelete = (rollupId: string) => {
    // If the deleted rollup column is the active Reference Column, reset
    // Compare-to-Total to Overall Total and inform the researcher.
    const deleted = currentConfig.crossCategoryRollups.find(
      (r) => r.id === rollupId,
    );
    const deletedCrossVariableId = deleted?.name
      ? columnVariableIds[deleted.name]
      : undefined;
    if (
      deletedCrossVariableId &&
      currentConfig.statisticalTesting?.referenceColumnEntity ===
        deletedCrossVariableId
    ) {
      updateReferenceColumnEntity(null);
      dispatch(
        showSnackbar({
          message:
            "The reference column was reset to Overall Total because its column rollup was deleted.",
          severity: "info",
        }),
      );
    }

    deleteCrossCategoryRollup(rollupId);
    handleCrossCategoryRollupsChange(
      currentConfig.crossCategoryRollups.filter((r) => r.id !== rollupId),
    );
  };

  const dispatch = useAppDispatch();

  const { data: datasetDetails } = useGetDatasetByIdQuery(
    currentConfig.datasetId as string,
    { skip: !currentConfig.datasetId },
  );

  const pitGuardrails = useMemo(() => {
    return (
      datasetDetails?.dateGuardrails?.pointInTime ??
      buildDefaultDateGuardrails().pointInTime
    );
  }, [datasetDetails?.dateGuardrails]);

  const currentPitUnit = useMemo(
    () => getPointInTimeUnitForPeriod(currentConfig.timePeriodSelection?.timePeriod ?? ""),
    [currentConfig.timePeriodSelection?.timePeriod],
  );

  const pitUnitGuardrail = currentPitUnit
    ? pitGuardrails.units[currentPitUnit]
    : null;

  const pitGuardrailAllowedWindowSizes: number[] | undefined =
    pitUnitGuardrail?.allowedRollingAverages.filter((n) => n !== 0);

  // "Apply Rolling Average" checkbox is forced OFF (disabled) only when the
  // dataset allows ZERO smoothed window sizes for this PIT unit (i.e. only 0
  // is allowed). When the default is 0 but other periods are also allowed,
  // the researcher must still be able to opt into smoothing manually.
  const isRollingAverageDisabledByPolicy: boolean =
    !!pitUnitGuardrail && (pitGuardrailAllowedWindowSizes?.length ?? 0) === 0;

  // "Apply Rolling Average" checkbox is forced ON (No-RA disabled) when 0 is
  // not in the allowed list (dataset requires smoothing for this unit).
  const pitGuardrailNoRollingDisabled: boolean = pitUnitGuardrail
    ? !pitUnitGuardrail.allowedRollingAverages.includes(
        0 as GuardrailRollingOption,
      )
    : false;

  const pitGuardrailConflict = useMemo((): string | null => {
    if (!pitUnitGuardrail || !currentPitUnit) return null;
    const isRollingEnabled = currentConfig.rollingAverage?.enabled ?? false;
    const windowSize = (currentConfig.rollingAverage?.windowSize ??
      0) as GuardrailRollingOption;
    const effectiveOption: GuardrailRollingOption = isRollingEnabled
      ? windowSize
      : 0;
    if (!pitUnitGuardrail.allowedRollingAverages.includes(effectiveOption)) {
      return isRollingEnabled
        ? `Rolling average of ${windowSize} periods is no longer allowed for ${currentPitUnit} by dataset policy.`
        : `No Rolling Average is no longer allowed for ${currentPitUnit} by dataset policy.`;
    }
    return null;
  }, [pitUnitGuardrail, currentPitUnit, currentConfig.rollingAverage]);

  const currentPitPeriod = VIEWER_PIT_PERIODS.has(
    currentConfig.timePeriodSelection?.timePeriod ?? "",
  )
    ? (currentConfig.timePeriodSelection
        ?.timePeriod as ViewerSelectableTimePeriod)
    : null;
  const isViewerOptionsInvalid =
    currentPitPeriod !== null &&
    currentConfig.viewerOptions.enabled &&
    Object.keys(currentConfig.viewerOptions.additionalPeriods).length === 0 &&
    currentConfig.viewerOptions.chartPeriodAdditionalRollingAverages.length ===
      0;

  const dashboardDateOverride = useMemo(() => {
    if (!usePreviewDateFilter) return undefined;
    const params = new URLSearchParams(window.location.search);
    const dfStart = params.get("df_start");
    const dfEnd = params.get("df_end");
    if (dfStart && dfEnd) return { startDate: dfStart, endDate: dfEnd };
    return undefined;
  }, [usePreviewDateFilter]);

  const isNoPeriodAvailable = useMemo(() => {
    if (!dashboardDateOverride?.startDate || !dashboardDateOverride?.endDate)
      return false;
    const timePeriod = currentConfig.timePeriodSelection?.timePeriod as
      | TimePeriod
      | undefined;
    if (
      !timePeriod ||
      timePeriod === "all_time" ||
      timePeriod === "custom_period"
    )
      return false;
    return !resolveTimePeriodInWindow(
      timePeriod,
      dashboardDateOverride.startDate,
      dashboardDateOverride.endDate,
    );
  }, [dashboardDateOverride, currentConfig.timePeriodSelection?.timePeriod]);

  const noPeriodMessage = useMemo(() => {
    if (!isNoPeriodAvailable) return null;
    const timePeriod = currentConfig.timePeriodSelection?.timePeriod ?? "";
    const unit = timePeriod.includes("month")
      ? "month"
      : timePeriod.includes("semester")
        ? "semester"
        : "quarter";
    return `No complete ${unit} available within the selected date range.`;
  }, [isNoPeriodAvailable, currentConfig.timePeriodSelection?.timePeriod]);

  // Check if statistical testing is enabled but no previous period data exists
  // Crosstab data has statSignificance in _metrics field
  const hasNoPreviousPeriodData =
    !isFetchingCrosstabData &&
    currentConfig.statisticalTesting?.enabled &&
    crosstabPreviewData &&
    crosstabPreviewData.length > 0 &&
    crosstabPreviewData.every((row) => {
      const metrics = (row as any)._metrics;
      if (!metrics) return true;
      return Object.values(metrics).every((metric: any) => {
        return (
          metric?.statSignificance === undefined ||
          metric?.statSignificance === null
        );
      });
    });

  const hasSampleSizeBelowThreshold =
    !isFetchingCrosstabData &&
    currentConfig.statisticalTesting?.enabled &&
    crosstabPreviewData &&
    crosstabPreviewData.length > 0 &&
    crosstabPreviewData
      .filter((row) => {
        const rowId = String(row.id || row.label);
        return !currentConfig.hiddenRows?.includes(rowId);
      })
      .some((row) => {
        const metrics = (row as any)._metrics;
        if (!metrics) return false;
        return Object.entries(metrics)
          .filter(([key]) => !currentConfig.hiddenColumns?.includes(key))
          .some(
            ([, metric]: [string, any]) =>
              metric.baseN !== undefined &&
              metric.baseN <
                currentConfig.statisticalTesting.minimumSampleSize &&
              // Only show warning if statistical testing is actually being performed on this cell
              (metric.statSignificance !== undefined ||
                metric.rowStatSignificance !== undefined),
          );
      });

  return (
    <>
      <Drawer
        anchor="right"
        open={isDrawerOpen}
        onClose={() => closeChartDrawer()}
        sx={{
          "& .MuiDrawer-paper": {
            width: "80vw",
            boxSizing: "border-box",
          },
        }}
      >
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          {/* Header */}
          <Box
            sx={{
              p: 2,
              borderBottom: 1,
              borderColor: "divider",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div className="flex-1">
              <Typography className="font-bold" variant="h6">
                {currentConfig.rowQuestion && currentConfig.columnQuestion
                  ? `${currentConfig.rowQuestion.questionLabel} × ${currentConfig.columnQuestion.questionLabel}`
                  : "Crosstab Configuration"}
              </Typography>
              <Typography variant="body1">
                {(() => {
                  if (
                    !currentConfig.rowQuestion ||
                    !currentConfig.columnQuestion
                  ) {
                    return "Select row and column questions to create a crosstab analysis";
                  }

                  const baseTitle = `${currentConfig.rowQuestion.questionTitle} by ${currentConfig.columnQuestion.questionTitle}`;

                  const rollingSuffix =
                    currentConfig.rollingAverage?.enabled && currentConfig.rollingAverage?.windowSize
                      ? getRollingAverageTitleSuffix(currentConfig.rollingAverage.windowSize)
                      : "";

                  // Check if user has customized the title
                  const hasCustomTitle =
                    currentConfig.title !== "" &&
                    currentConfig.title !== `Crosstab ${baseTitle}`;

                  // If title is customized, show the custom title
                  if (hasCustomTitle) {
                    return currentConfig.title;
                  }

                  // If title is NOT customized and time period is set, append it
                  if (currentConfig.timePeriodSelection) {
                    const timePeriodSuffix = getTimePeriodTitleSuffix(
                      currentConfig.timePeriodSelection.timePeriod,
                      currentConfig.timePeriodSelection.startDate,
                      currentConfig.timePeriodSelection.endDate,
                    );

                    if (timePeriodSuffix) {
                      return `${baseTitle}${timePeriodSuffix}${rollingSuffix}`;
                    }
                  }

                  return `${baseTitle}${rollingSuffix}`;
                })()}
              </Typography>
              {currentConfig.datasetName && (
                <Typography variant="caption" color="text.secondary">
                  Dataset: {currentConfig.datasetName}
                </Typography>
              )}
            </div>
            {(() => {
              const hasDashboardDateFilters = !!(
                new URLSearchParams(window.location.search).get("df_start") &&
                new URLSearchParams(window.location.search).get("df_end")
              );
              return (
                <>
                  <DrawerFilterToggles
                    hasDashboardDateFilters={hasDashboardDateFilters}
                    usePreviewDateFilter={usePreviewDateFilter}
                    onPreviewDateFilterToggle={handlePreviewDateFilterToggle}
                    activeFiltersCount={activeFilters.length}
                    localApplyFilters={localApplyFilters}
                    onFilterToggle={handleFilterToggle}
                    disabled={isTogglingFilters || isFetchingCrosstabData}
                  />
                </>
              );
            })()}
          </Box>

          {hasHiddenContent && (
            <ChartHiddenContentWarning datasetId={currentConfig.datasetId} />
          )}

          {/* Main Content - Side by Side Layout */}
          <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
            {/* Configuration Panel */}
            <Box
              sx={{
                width: CONFIG_PANEL_WIDTH,
                borderRight: 1,
                borderColor: "divider",
                overflow: "auto",
                p: 3,
              }}
            >
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 700 }}>
                Configurations
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {/* Row Question Selection - Accordion */}
                <Accordion
                  sx={{
                    boxShadow: "none",
                    "&:before": { display: "none" },
                    border: "1px solid #e0e0e0",
                    borderRadius: "4px !important",
                    "&.Mui-expanded": { margin: 0 },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      minHeight: "48px",
                      "&.Mui-expanded": { minHeight: "48px" },
                      "& .MuiAccordionSummary-content": {
                        margin: "8px 0",
                        "&.Mui-expanded": { margin: "8px 0" },
                      },
                    }}
                  >
                    <Box
                      sx={{ display: "flex", flexDirection: "column", flex: 1 }}
                    >
                      <Typography sx={{ fontWeight: 600, fontSize: "14px" }}>
                        Row Question
                      </Typography>
                      {currentConfig.rowQuestion && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mt: 0.5,
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            {currentConfig.rowQuestion.questionLabel}
                          </Typography>
                          {isRowQuestionHidden && <HiddenInDatasetBadge />}
                        </Box>
                      )}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    <Box>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Search row questions..."
                        value={rowQuestionSelection.searchTerm}
                        onChange={rowQuestionSelection.handleSearchChange}
                        disabled={
                          !currentConfig.datasetId ||
                          isFetchingCrosstabData ||
                          isTogglingFilters
                        }
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ mb: 1 }}
                      />
                      <Box sx={{ position: "relative" }}>
                        <QuestionsList
                          questions={rowQuestionSelection.questions.filter(
                            (q) =>
                              !currentConfig.columnQuestion ||
                              q.datasetElementId !==
                                currentConfig.columnQuestion.questionId,
                          )}
                          selectedQuestionId={
                            currentConfig.rowQuestion?.questionId || ""
                          }
                          onQuestionSelect={(question) => {
                            if (!isFetchingCrosstabData && !isTogglingFilters) {
                              handleRowQuestionSelect(
                                rowQuestionSelection.convertToQuestionSelection(
                                  question,
                                ),
                              );
                            }
                          }}
                          searchTerm={rowQuestionSelection.searchTerm}
                          isFetching={rowQuestionSelection.isFetching}
                          hasMore={rowQuestionSelection.hasMore}
                          observerTarget={rowQuestionSelection.observerTarget}
                          recentQuestionIds={
                            rowQuestionSelection.recentQuestionIds
                          }
                          compact={true}
                        />
                        {(isFetchingCrosstabData || isTogglingFilters) && (
                          <Box
                            sx={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: "rgba(255, 255, 255, 0.8)",
                              zIndex: 1,
                              pointerEvents: "all",
                              cursor: "not-allowed",
                            }}
                          />
                        )}
                      </Box>

                      {/* Row Focus Type Selection */}
                      {rowNeedsFocus &&
                        (isRowFocusBlocked ? (
                          <Alert severity="warning" sx={{ mt: 2 }}>
                            All variables or values for this question are
                            hidden in the dataset, so Row Focus can&apos;t be
                            configured. Restore content in Questions to
                            continue.
                          </Alert>
                        ) : (
                          <FocusSelector
                            label="Row Focus"
                            focusConfig={currentConfig.rowFocus}
                            variables={rowVariables}
                            onFocusTypeChange={handleRowFocusTypeChange}
                            onVariableSelect={handleRowVariableSelect}
                            onValueSelect={handleRowValueSelect}
                            disabled={
                              isFetchingCrosstabData || isTogglingFilters
                            }
                            onFocusMetricChange={handleRowFocusMetricChange}
                            onRollupNameChange={handleRowRollupNameChange}
                            onRollupLabelsChange={handleRowRollupLabelsChange}
                          />
                        ))}
                    </Box>
                  </AccordionDetails>
                </Accordion>

                {/* Column Question Selection - Accordion */}
                <Accordion
                  sx={{
                    boxShadow: "none",
                    "&:before": { display: "none" },
                    border: "1px solid #e0e0e0",
                    borderRadius: "4px !important",
                    "&.Mui-expanded": { margin: 0 },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      minHeight: "48px",
                      "&.Mui-expanded": { minHeight: "48px" },
                      "& .MuiAccordionSummary-content": {
                        margin: "8px 0",
                        "&.Mui-expanded": { margin: "8px 0" },
                      },
                    }}
                  >
                    <Box
                      sx={{ display: "flex", flexDirection: "column", flex: 1 }}
                    >
                      <Typography sx={{ fontWeight: 600, fontSize: "14px" }}>
                        Column Question
                      </Typography>
                      {currentConfig.columnQuestion && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mt: 0.5,
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            {currentConfig.columnQuestion.questionLabel}
                          </Typography>
                          {isColumnQuestionHidden && <HiddenInDatasetBadge />}
                        </Box>
                      )}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    <Box>
                      <Box sx={{ position: "relative" }}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Search column questions..."
                          value={columnQuestionSelection.searchTerm}
                          onChange={columnQuestionSelection.handleSearchChange}
                          disabled={
                            !currentConfig.datasetId ||
                            !currentConfig.rowQuestion ||
                            isFetchingCrosstabData ||
                            isTogglingFilters
                          }
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                              </InputAdornment>
                            ),
                          }}
                          sx={{ mb: 1 }}
                        />
                        <Box sx={{ position: "relative" }}>
                          <QuestionsList
                            questions={columnQuestionSelection.questions.filter(
                              (q) =>
                                !currentConfig.rowQuestion ||
                                q.datasetElementId !==
                                  currentConfig.rowQuestion?.questionId,
                            )}
                            selectedQuestionId={
                              currentConfig.columnQuestion?.questionId || ""
                            }
                            onQuestionSelect={(question) => {
                              if (
                                currentConfig.rowQuestion &&
                                !isFetchingCrosstabData &&
                                !isTogglingFilters
                              ) {
                                handleColumnQuestionSelect(
                                  columnQuestionSelection.convertToQuestionSelection(
                                    question,
                                  ),
                                );
                              }
                            }}
                            searchTerm={columnQuestionSelection.searchTerm}
                            isFetching={columnQuestionSelection.isFetching}
                            hasMore={columnQuestionSelection.hasMore}
                            observerTarget={
                              columnQuestionSelection.observerTarget
                            }
                            recentQuestionIds={
                              columnQuestionSelection.recentQuestionIds
                            }
                            compact={true}
                          />
                          {!currentConfig.rowQuestion && (
                            <Box
                              sx={{
                                position: "absolute",
                                inset: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: "rgba(255, 255, 255, 0.95)",
                                border: "1px solid #e0e0e0",
                                borderRadius: 1,
                                zIndex: 1,
                                pointerEvents: "all",
                                cursor: "not-allowed",
                              }}
                            >
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Please select a row question first
                              </Typography>
                            </Box>
                          )}
                          {(isFetchingCrosstabData || isTogglingFilters) &&
                            currentConfig.rowQuestion && (
                              <Box
                                sx={{
                                  position: "absolute",
                                  inset: 0,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                                  zIndex: 2,
                                  pointerEvents: "all",
                                  cursor: "not-allowed",
                                }}
                              />
                            )}
                        </Box>
                      </Box>

                      {/* Column Focus Type Selection */}
                      {columnNeedsFocus &&
                        (isColumnFocusBlocked ? (
                          <Alert severity="warning" sx={{ mt: 2 }}>
                            All variables or values for this question are
                            hidden in the dataset, so Column Focus can&apos;t
                            be configured. Restore content in Questions to
                            continue.
                          </Alert>
                        ) : (
                          <FocusSelector
                            label="Column Focus"
                            focusConfig={currentConfig.columnFocus}
                            variables={columnVariables}
                            onFocusTypeChange={handleColumnFocusTypeChange}
                            onVariableSelect={handleColumnVariableSelect}
                            onValueSelect={handleColumnValueSelect}
                            disabled={
                              isFetchingCrosstabData || isTogglingFilters
                            }
                            onFocusMetricChange={handleColumnFocusMetricChange}
                            onRollupNameChange={handleColumnRollupNameChange}
                            onRollupLabelsChange={
                              handleColumnRollupLabelsChange
                            }
                            rollupOptionDisabled={columnRollupWeightUnsupported}
                            rollupDisabledMessage={
                              columnRollupWeightDisabledMessage
                            }
                          />
                        ))}
                    </Box>
                  </AccordionDetails>
                </Accordion>

                <Divider />

                {/* Display Options */}
                <Box>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    <FormControl fullWidth size="small">
                      <FormLabel className="text-[12px] text-[#262626]">
                        Rounding
                      </FormLabel>
                      <Select
                        disabled={isFetchingCrosstabData || isTogglingFilters}
                        value={currentConfig.percentageFormatting}
                        onChange={(e) =>
                          updatePercentageFormatting(Number(e.target.value))
                        }
                      >
                        <MenuItem value={0}>0 decimals</MenuItem>
                        <MenuItem value={1}>1 decimal</MenuItem>
                        <MenuItem value={2}>2 decimals</MenuItem>
                      </Select>
                    </FormControl>

                    <FormControl fullWidth size="small">
                      <FormLabel className="text-[12px] text-[#262626]">
                        Weight
                      </FormLabel>
                      <Select
                        disabled={isFetchingCrosstabData || isTogglingFilters}
                        value={
                          currentConfig.weightOption === "custom" &&
                          currentConfig.customWeight
                            ? `custom_${currentConfig.customWeight.questionId}_${currentConfig.customWeight.variableId}`
                            : currentConfig.weightOption
                        }
                        onChange={(e) => handleWeightChange(e.target.value)}
                      >
                        {weightOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {currentConfig.weightOption === "none" && (
                      <Alert
                        severity="warning"
                        icon={<WarningIcon fontSize="small" />}
                        sx={{
                          fontSize: "0.75rem",
                          "& .MuiAlert-message": { fontSize: "0.75rem" },
                        }}
                      >
                        Weights disabled: unweighted counts shown.
                      </Alert>
                    )}

                    {/* Per-Segment Weight Configuration */}
                    {/* Show per-segment config when weight option is selected, even if preview data isn't loaded yet */}
                    {currentConfig.weightOption === "per-segment" &&
                      currentConfig.datasetId && (
                        <PerSegmentWeightConfig
                          datasetId={currentConfig.datasetId}
                          columnLabels={
                            crosstabPreviewData &&
                            crosstabPreviewData.length > 0
                              ? Object.keys(
                                  crosstabPreviewData[0] || {},
                                ).filter(
                                  (key) =>
                                    key !== "id" &&
                                    key !== "label" &&
                                    key !== "_metrics" &&
                                    key !== "enableStatTesting" &&
                                    key !== "enableRowStatTesting",
                                )
                              : []
                          }
                          customColumnLabels={
                            currentConfig.customColumnLabels || {}
                          }
                          hiddenColumns={currentConfig.hiddenColumns || []}
                          columnVariableIds={columnVariableIds}
                          onWeightsChange={updatePerSegmentWeights}
                          disabled={isFetchingCrosstabData || isTogglingFilters}
                        />
                      )}

                    <FormControlLabel
                      control={
                        <Checkbox
                          disabled={isFetchingCrosstabData || isTogglingFilters}
                          checked={currentConfig.showN}
                          onChange={(e) => updateShowN(e.target.checked)}
                          size="small"
                        />
                      }
                      label="Show N (sample size)"
                    />

                    <FormControl fullWidth size="small">
                      <FormLabel className="text-[12px] text-[#262626] mb-1">
                        Minimum Base Size
                      </FormLabel>
                      <TextField
                        disabled={isFetchingCrosstabData || isTogglingFilters}
                        type="text"
                        value={String(localMinimumBaseSize)}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Only allow numbers (no letters, signs, or special characters)
                          if (value === "" || /^\d+$/.test(value)) {
                            const numValue = value === "" ? 0 : Number(value);
                            setLocalMinimumBaseSize(numValue);
                            updateMinimumBaseSize(numValue);
                          }
                        }}
                        size="small"
                        fullWidth
                        inputProps={{
                          inputMode: "numeric",
                          pattern: "[0-9]*",
                        }}
                        sx={{
                          "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                            {
                              display: "none",
                            },
                          "& input[type=number]": {
                            MozAppearance: "textfield",
                          },
                        }}
                      />
                      <FormHelperText>
                        Cells with base size below this value will be suppressed
                      </FormHelperText>
                    </FormControl>
                  </Box>
                </Box>

                <Divider />

                <ColorCodingSection
                  metricType={getMetricTypeFromQuestionType(
                    currentConfig.rowQuestion?.questionType,
                  )}
                  selectedTemplateId={currentConfig.colorCodingTemplateId}
                  onTemplateChange={updateColorCodingTemplate}
                />

                <Divider />

                <BaseSection
                  datasetId={currentConfig.datasetId}
                  isDisabled={isFetchingCrosstabData}
                  base={visualizationBase}
                  onChange={onVisualizationBaseChange}
                />

                <Divider />

                {/* Time Period & Rolling Average */}
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Time Period
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                      mt: 2,
                    }}
                  >
                    <TimePeriodSelection
                      timePeriodSelection={currentConfig.timePeriodSelection}
                      onTimePeriodChange={handleTimePeriodChange}
                      onTimePeriodDatesChange={handleTimePeriodDatesChange}
                      statisticalTestingEnabled={
                        currentConfig.statisticalTesting?.enabled || false
                      }
                      responsesMinDate={currentConfig.responsesMinDate}
                      responsesMaxDate={currentConfig.responsesMaxDate}
                      disabled={isFetchingCrosstabData || isTogglingFilters}
                      datasetId={currentConfig.datasetId}
                    />

                    <RollingAverageSection
                      enabled={currentConfig.rollingAverage?.enabled || false}
                      windowSize={currentConfig.rollingAverage?.windowSize || 2}
                      timePeriod={
                        currentConfig.timePeriodSelection?.timePeriod ||
                        "all_time"
                      }
                      guardrailAllowedWindowSizes={
                        pitGuardrailAllowedWindowSizes
                      }
                      guardrailRollingDisabled={
                        isRollingAverageDisabledByPolicy
                      }
                      guardrailNoRollingDisabled={pitGuardrailNoRollingDisabled}
                      guardrailConflictWarning={pitGuardrailConflict ?? undefined}
                      disabled={isFetchingCrosstabData || isTogglingFilters}
                      onEnabledChange={(enabled) => {
                        updateRollingAverageEnabled(enabled);
                        handleRollingAverageChange(
                          enabled
                            ? currentConfig.rollingAverage?.windowSize || 2
                            : null,
                        );
                      }}
                      onWindowSizeChange={(size) => {
                        updateRollingAverageWindowSize(size);
                        handleRollingAverageChange(size);
                      }}
                    />

                    <PointInTimeViewerOptionsSection
                      viewerOptions={currentConfig.viewerOptions}
                      chartTimePeriod={currentPitPeriod}
                      chartRollingWindowSize={
                        currentConfig.rollingAverage?.enabled
                          ? (currentConfig.rollingAverage?.windowSize ?? 0)
                          : 0
                      }
                      pitGuardrailUnits={pitGuardrails.units}
                      isDisabled={isFetchingCrosstabData || isTogglingFilters}
                      onViewerOptionsChange={(opts) =>
                        dispatch(updateViewerOptions(opts))
                      }
                      datasetId={currentConfig.datasetId ?? null}
                      onRestoreSharedTime={(config) => {
                        const d = crosstabConfigDispatcher(dispatch);
                        d.updateTimePeriod(
                          config.savedDefault as Parameters<
                            typeof d.updateTimePeriod
                          >[0],
                        );
                        const ra = config.savedRollingAverage;
                        d.updateRollingAverageEnabled(ra > 0);
                        if (ra > 0) d.updateRollingAverageWindowSize(ra);
                        if (config.viewerOptions) {
                          dispatch(
                            updateViewerOptions(
                              config.viewerOptions as typeof currentConfig.viewerOptions,
                            ),
                          );
                        }
                      }}
                    />
                  </Box>
                </Box>

                <Divider />

                {/* Statistical Testing */}
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Statistical Testing
                  </Typography>
                  {currentConfig.statisticalTesting?.enabled &&
                    overlapTestingUnavailable && (
                      <Alert severity="warning" sx={{ mt: 1 }}>
                        Some significance markers are unavailable because one or
                        more comparisons could not be evaluated with the required
                        overlap-aware method.
                      </Alert>
                    )}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                      mt: 2,
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          disabled={
                            isFetchingCrosstabData ||
                            isTogglingFilters ||
                            isAllTimePeriod
                          }
                          checked={
                            currentConfig.statisticalTesting?.enabled || false
                          }
                          onChange={(e) =>
                            handleStatTestingChange(
                              () => updateStatTestingEnabled(e.target.checked),
                              {
                                enabled: e.target.checked,
                              },
                            )
                          }
                          size="small"
                        />
                      }
                      label="Enable Statistical Testing"
                    />

                    {currentConfig.statisticalTesting?.enabled && (
                      <>
                        <FormControl fullWidth size="small">
                          <FormLabel className="text-[12px] text-[#262626]">
                            Test Type
                          </FormLabel>
                          <Select
                            disabled={
                              isFetchingCrosstabData || isTogglingFilters
                            }
                            value={
                              currentConfig.statisticalTesting?.testType ||
                              "z-test"
                            }
                            onChange={(e) =>
                              handleStatTestingChange(
                                () =>
                                  updateStatTestType(
                                    e.target.value as StatTestType,
                                  ),
                                { testType: e.target.value as StatTestType },
                              )
                            }
                          >
                            <MenuItem value="z-test">
                              Z-Test (Proportions)
                            </MenuItem>
                            <MenuItem value="t-test">
                              T-Test (with Overlap)
                            </MenuItem>
                          </Select>
                        </FormControl>

                        <FormControl fullWidth size="small">
                          <FormLabel className="text-[12px] text-[#262626]">
                            Significance Level
                          </FormLabel>
                          <Select
                            disabled={
                              isFetchingCrosstabData || isTogglingFilters
                            }
                            value={
                              currentConfig.statisticalTesting
                                ?.significanceLevel || "95%"
                            }
                            onChange={(e) =>
                              handleStatTestingChange(
                                () =>
                                  updateSignificanceLevel(
                                    e.target.value as SignificanceLevel,
                                  ),
                                {
                                  significanceLevel: e.target
                                    .value as SignificanceLevel,
                                },
                              )
                            }
                          >
                            <MenuItem value="90%">90% Confidence</MenuItem>
                            <MenuItem value="95%">95% Confidence</MenuItem>
                            <MenuItem value="99%">99% Confidence</MenuItem>
                          </Select>
                        </FormControl>

                        <FormControl fullWidth size="small">
                          <FormLabel className="text-[12px] text-[#262626] mb-1">
                            Minimum Sample Size
                          </FormLabel>
                          <TextField
                            disabled={
                              isFetchingCrosstabData || isTogglingFilters
                            }
                            type="text"
                            value={String(
                              currentConfig.statisticalTesting
                                ?.minimumSampleSize || 30,
                            )}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === "" || /^\d+$/.test(value)) {
                                const newSize =
                                  value === "" ? 0 : Number(value);
                                handleStatTestingChange(
                                  () => updateMinimumSampleSize(newSize),
                                  { minimumSampleSize: newSize },
                                );
                              }
                            }}
                            size="small"
                            fullWidth
                            inputProps={{
                              inputMode: "numeric",
                              pattern: "[0-9]*",
                            }}
                            sx={{
                              "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                                {
                                  display: "none",
                                },
                              "& input[type=number]": {
                                MozAppearance: "textfield",
                              },
                            }}
                          />
                          <FormHelperText>
                            Minimum sample size required for statistical testing
                          </FormHelperText>
                        </FormControl>

                        <FormControlLabel
                          control={
                            <Checkbox
                              disabled={
                                isFetchingCrosstabData || isTogglingFilters
                              }
                              checked={
                                currentConfig.statisticalTesting
                                  ?.compareToTotal || false
                              }
                              onChange={(e) =>
                                handleStatTestingChange(
                                  () => updateCompareToTotal(e.target.checked),
                                  { compareToTotal: e.target.checked },
                                )
                              }
                              size="small"
                            />
                          }
                          label="Enable Compare to Total Statistical Testing"
                        />

                        {/* Compare to Total Reference Selection */}
                        {currentConfig.statisticalTesting?.compareToTotal && (
                          <Box sx={{ ml: 2, mt: 2 }}>
                            <FormControl component="fieldset" size="small">
                              <FormLabel className="text-[12px] text-[#262626] mb-1">
                                Compare to Total Reference
                              </FormLabel>
                              <RadioGroup
                                value={
                                  currentConfig.statisticalTesting
                                    ?.referenceColumnEntity === null
                                    ? "overall_total"
                                    : "reference_column"
                                }
                                onChange={(e) => {
                                  const isOverallTotal =
                                    e.target.value === "overall_total";

                                  if (isOverallTotal) {
                                    // For Overall Total, make the request with null
                                    handleStatTestingChange(
                                      () => updateReferenceColumnEntity(null),
                                      { referenceColumnEntity: null },
                                    );
                                  } else {
                                    // For Reference Column, just update state without request
                                    // Request will happen when user selects a column from dropdown
                                    updateReferenceColumnEntity("");
                                  }
                                }}
                              >
                                <FormControlLabel
                                  value="overall_total"
                                  control={
                                    <Radio
                                      size="small"
                                      disabled={
                                        isFetchingCrosstabData ||
                                        isTogglingFilters
                                      }
                                    />
                                  }
                                  label={
                                    <Box>
                                      <Typography variant="body2">
                                        Overall Total
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        Compare each column against the row
                                        total
                                      </Typography>
                                    </Box>
                                  }
                                />
                                <FormControlLabel
                                  value="reference_column"
                                  control={
                                    <Radio
                                      size="small"
                                      disabled={
                                        isFetchingCrosstabData ||
                                        isTogglingFilters
                                      }
                                    />
                                  }
                                  label={
                                    <Box>
                                      <Typography variant="body2">
                                        Reference Column
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        Compare each column against a specific
                                        reference column
                                      </Typography>
                                    </Box>
                                  }
                                />
                              </RadioGroup>
                            </FormControl>

                            {/* Reference Column Dropdown */}
                            {currentConfig.statisticalTesting
                              ?.referenceColumnEntity !== null && (
                              <FormControl
                                fullWidth
                                size="small"
                                sx={{ mt: 2 }}
                              >
                                <FormLabel className="text-[12px] text-[#262626] mb-1">
                                  Select Reference Column
                                </FormLabel>
                                <Select
                                  disabled={
                                    isFetchingCrosstabData ||
                                    isTogglingFilters ||
                                    !crosstabPreviewData ||
                                    crosstabPreviewData.length === 0
                                  }
                                  value={
                                    currentConfig.statisticalTesting
                                      ?.referenceColumnEntity || ""
                                  }
                                  onChange={(e) =>
                                    handleStatTestingChange(
                                      () =>
                                        handleReferenceColumnSelect(
                                          e.target.value || null,
                                        ),
                                      { referenceColumnEntity: e.target.value },
                                    )
                                  }
                                  displayEmpty
                                >
                                  <MenuItem value="">
                                    <em>Select a column...</em>
                                  </MenuItem>
                                  {crosstabPreviewData &&
                                    crosstabPreviewData.length > 0 &&
                                    Object.keys(crosstabPreviewData[0] || {})
                                      .filter(
                                        (key) =>
                                          key !== "id" &&
                                          key !== "label" &&
                                          key !== "_metrics" &&
                                          key !== "enableStatTesting" &&
                                          key !== "enableRowStatTesting",
                                      )
                                      // New/replacement picks offer only visible columns —
                                      // the currently-saved reference column stays offered
                                      // even if now hidden, so it doesn't vanish mid-edit.
                                      .filter((columnLabel) => {
                                        if (!hiddenColumnLabels.has(columnLabel))
                                          return true;
                                        const columnVarId =
                                          columnVariableIds[columnLabel] ||
                                          columnLabel;
                                        return (
                                          currentConfig.statisticalTesting
                                            ?.referenceColumnEntity ===
                                          columnVarId
                                        );
                                      })
                                      .map((columnLabel) => {
                                        const columnVarId =
                                          columnVariableIds[columnLabel] ||
                                          columnLabel;
                                        const isHidden =
                                          currentConfig.hiddenColumns.includes(
                                            columnLabel,
                                          );
                                        const isHiddenInDataset =
                                          hiddenColumnLabels.has(columnLabel);
                                        const customLabel =
                                          currentConfig.customColumnLabels?.[
                                            columnLabel
                                          ];
                                        const displayLabel =
                                          customLabel || columnLabel;

                                        return (
                                          <MenuItem
                                            key={columnVarId}
                                            value={columnVarId}
                                          >
                                            <Box
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1,
                                              }}
                                            >
                                              {displayLabel}
                                              {isHidden && (
                                                <Typography
                                                  component="span"
                                                  variant="caption"
                                                  sx={{
                                                    color: "text.secondary",
                                                    fontStyle: "italic",
                                                  }}
                                                >
                                                  (hidden)
                                                </Typography>
                                              )}
                                              {isHiddenInDataset && (
                                                <HiddenInDatasetBadge size="small" />
                                              )}
                                            </Box>
                                          </MenuItem>
                                        );
                                      })}
                                </Select>
                                <FormHelperText>
                                  The reference column will be automatically
                                  unhidden and cannot be hidden while in use
                                </FormHelperText>
                              </FormControl>
                            )}
                          </Box>
                        )}

                        <Box
                          sx={{
                            p: 2,
                            backgroundColor: "#f5f5f5",
                            borderRadius: 1,
                            border: "1px solid #e0e0e0",
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 600, display: "block", mb: 1 }}
                          >
                            Statistical Markers Legend:
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ display: "block" }}
                          >
                            <strong>▲</strong> Significantly higher than
                            previous period
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ display: "block" }}
                          >
                            <strong>▼</strong> Significantly lower than previous
                            period
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ display: "block" }}
                          >
                            <strong>+</strong> Significantly higher than total
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ display: "block" }}
                          >
                            <strong>−</strong> Significantly lower than total
                          </Typography>
                        </Box>
                      </>
                    )}

                    <Typography variant="caption" color="text.secondary">
                      Statistical testing helps identify significant differences
                      in your data
                    </Typography>
                  </Box>
                </Box>

                <Divider />

                {/* Old Accordion version - removed */}
                {/* <Accordion
                  disabled
                  sx={{ boxShadow: "none", border: "1px solid #eee" }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Statistical Testing
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={
                              currentConfig.statisticalTesting?.enabled || false
                            }
                            onChange={(e) =>
                              updateStatTestingEnabled(e.target.checked)
                            }
                            size="small"
                          />
                        }
                        label="Enable Statistical Testing"
                      />

                      {currentConfig.statisticalTesting?.enabled && (
                        <>
                          <FormControl fullWidth size="small">
                            <FormLabel className="text-[12px] text-[#262626]">
                              Test Type
                            </FormLabel>
                            <Select
                              value={
                                currentConfig.statisticalTesting?.testType ||
                                "z-test"
                              }
                              onChange={(e) =>
                                updateStatTestType(
                                  e.target.value as StatTestType,
                                )
                              }
                            >
                              <MenuItem value="z-test">
                                Z-Test (Proportions)
                              </MenuItem>
                            </Select>
                          </FormControl>

                          <FormControl fullWidth size="small">
                            <FormLabel className="text-[12px] text-[#262626]">
                              Significance Level
                            </FormLabel>
                            <Select
                              value={
                                currentConfig.statisticalTesting
                                  ?.significanceLevel || "95%"
                              }
                              onChange={(e) =>
                                updateSignificanceLevel(
                                  e.target.value as SignificanceLevel,
                                )
                              }
                            >
                              <MenuItem value="90%">90% Confidence</MenuItem>
                              <MenuItem value="95%">95% Confidence</MenuItem>
                              <MenuItem value="99%">99% Confidence</MenuItem>
                            </Select>
                          </FormControl>

                          <FormControl fullWidth size="small">
                            <FormLabel className="text-[12px] text-[#262626] mb-1">
                              Minimum Sample Size
                            </FormLabel>
                            <TextField
                              disabled={isFetchingCrosstabData}
                              type="text"
                              value={String(
                                currentConfig.statisticalTesting
                                  ?.minimumSampleSize || 30,
                              )}
                              onChange={(e) => {
                                const value = e.target.value;
                                // Only allow numbers (no letters, signs, or special characters)
                                if (value === "" || /^\d+$/.test(value)) {
                                  updateMinimumSampleSize(
                                    value === "" ? 0 : Number(value),
                                  );
                                }
                              }}
                              size="small"
                              fullWidth
                              inputProps={{
                                inputMode: "numeric",
                                pattern: "[0-9]*",
                              }}
                              sx={{
                                "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                                  {
                                    display: "none",
                                  },
                                "& input[type=number]": {
                                  MozAppearance: "textfield",
                                },
                              }}
                            />
                            <FormHelperText>
                              Minimum sample size required for statistical
                              testing
                            </FormHelperText>
                          </FormControl>

                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={
                                  currentConfig.statisticalTesting
                                    ?.compareToTotal || false
                                }
                                onChange={(e) =>
                                  updateCompareToTotal(e.target.checked)
                                }
                                size="small"
                              />
                            }
                            label="Compare to Total Column"
                          />

                          <Box
                            sx={{
                              p: 2,
                              backgroundColor: "#f5f5f5",
                              borderRadius: 1,
                              border: "1px solid #e0e0e0",
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ fontWeight: 600, display: "block", mb: 1 }}
                            >
                              Statistical Markers Legend:
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ display: "block" }}
                            >
                              <strong>▲</strong> Significantly higher than
                              previous period
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ display: "block" }}
                            >
                              <strong>▼</strong> Significantly lower than
                              previous period
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ display: "block" }}
                            >
                              <strong>+</strong> Significantly higher than total
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ display: "block" }}
                            >
                              <strong>−</strong> Significantly lower than total
                            </Typography>
                          </Box>
                        </>
                      )}

                      <Typography variant="caption" color="text.secondary">
                        Statistical testing helps identify significant
                        differences in your data
                      </Typography>
                    </Box>
                  </AccordionDetails>
                </Accordion> */}

                {/* Styling & Customization */}
                <Accordion
                  defaultExpanded
                  sx={{ boxShadow: "none", border: "1px solid #eee" }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Styling & Customization
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      <Box sx={{ position: "relative" }}>
                        <HPInput
                          disabled={isFetchingCrosstabData || isTogglingFilters}
                          label="Widget title"
                          value={currentConfig.title}
                          onChange={(e) => updateChartTitle(e.target.value)}
                        />
                        <Box
                          sx={{
                            position: "absolute",
                            bottom: "70%",
                            right: 0,
                            display: "flex",
                            gap: 0.5,
                          }}
                        >
                          <ToggleButton
                            disabled={
                              isFetchingCrosstabData || isTogglingFilters
                            }
                            value="bold"
                            selected={currentConfig.titleBold}
                            onChange={() =>
                              updateTitleBold(!currentConfig.titleBold)
                            }
                            size="small"
                            sx={{
                              width: 32,
                              height: 32,
                              minWidth: 32,
                              border: "1px solid #e0e0e0",
                              "&.Mui-selected": {
                                backgroundColor: "#1976d2",
                                color: "white",
                                "&:hover": {
                                  backgroundColor: "#1565c0",
                                },
                              },
                            }}
                          >
                            <FormatBoldIcon fontSize="small" />
                          </ToggleButton>
                          <ToggleButton
                            disabled={
                              isFetchingCrosstabData || isTogglingFilters
                            }
                            value="underline"
                            selected={currentConfig.titleUnderline}
                            onChange={() =>
                              updateTitleUnderline(
                                !currentConfig.titleUnderline,
                              )
                            }
                            size="small"
                            sx={{
                              width: 32,
                              height: 32,
                              minWidth: 32,
                              border: "1px solid #e0e0e0",
                              "&.Mui-selected": {
                                backgroundColor: "#1976d2",
                                color: "white",
                                "&:hover": {
                                  backgroundColor: "#1565c0",
                                },
                              },
                            }}
                          >
                            <FormatUnderlinedIcon fontSize="small" />
                          </ToggleButton>
                        </Box>
                      </Box>

                      <HPInput
                        disabled={isFetchingCrosstabData || isTogglingFilters}
                        label="Widget subtitle"
                        value={currentConfig.subtitle}
                        onChange={(e) => updateChartSubtitle(e.target.value)}
                        placeholder="Optional"
                      />
                    </Box>
                  </AccordionDetails>
                </Accordion>

                <Divider />

                {/* Row Rollups — hidden for Numeric/Float questions (no categorical rows to combine) */}
                {currentConfig.rowQuestion?.questionType !== "Float" &&
                  currentConfig.rowQuestion?.questionType !== "Number" && (
                    <>
                      <CategoryRollupsSection
                        categoryRollups={currentConfig.categoryRollups}
                        barOptions={rowRollupOptions}
                        onCreateRollup={handleCategoryRollupCreate}
                        onUpdateRollup={handleCategoryRollupUpdate}
                        onDeleteRollup={handleCategoryRollupDelete}
                        title="Row Rollups"
                        itemLabel="rows"
                        hiddenSourceLabels={hiddenRowSourceIds}
                      />

                      <CategoryRollupsNotice
                        categoryRollups={currentConfig.categoryRollups}
                        barOptions={rowRollupOptions}
                        variableIdLabelMap={currentConfig.variableIdLabelMap}
                        itemLabel="rows"
                        rollupLabel="row rollup"
                        chartLabel="crosstab"
                      />
                    </>
                  )}

                <Divider />

                {/* Column Rollups — mirrors row rollups, using crossCategoryRollups */}
                {currentConfig.columnQuestion && (
                  <>
                    <CategoryRollupsSection
                      categoryRollups={currentConfig.crossCategoryRollups}
                      barOptions={columnRollupOptions}
                      onCreateRollup={handleCrossCategoryRollupCreate}
                      onUpdateRollup={handleCrossCategoryRollupUpdate}
                      onDeleteRollup={handleCrossCategoryRollupDelete}
                      title="Column Rollups"
                      itemLabel="columns"
                      ineligibleSourceLabels={columnRollupIneligibleSources}
                      addDisabled={columnRollupWeightUnsupported}
                      addDisabledMessage={columnRollupWeightDisabledMessage}
                      hiddenSourceLabels={hiddenColumnSourceIds}
                    />

                    <CategoryRollupsNotice
                      categoryRollups={currentConfig.crossCategoryRollups}
                      barOptions={columnRollupOptions}
                      variableIdLabelMap={currentConfig.crossVariableIdLabelMap}
                      itemLabel="columns"
                      rollupLabel="column rollup"
                      chartLabel="crosstab"
                    />

                    <Divider />
                  </>
                )}

                {/* Row and Column Customization */}
                {crosstabPreviewData &&
                  crosstabPreviewData.length > 0 &&
                  (() => {
                    const previewRows = crosstabPreviewData.map(
                      (row, index) => ({
                        id: String(row.id || index),
                        label: String(row.label || ""),
                        type: "variable" as const,
                      }),
                    );

                    // Collect columns from ALL rows to handle cases where
                    // different rows have different numbers of descriptiveMetrics
                    const columnKeySet = new Set<string>();
                    crosstabPreviewData.forEach((row) => {
                      Object.keys(row).forEach((key) => {
                        if (
                          key !== "id" &&
                          key !== "label" &&
                          key !== "_metrics" &&
                          key !== "enableStatTesting" &&
                          key !== "enableRowStatTesting"
                        ) {
                          columnKeySet.add(key);
                        }
                      });
                    });
                    const previewColumns = Array.from(columnKeySet).map(
                      (key) => ({
                        id: key,
                        label: key,
                        type: "variable" as const,
                      }),
                    );

                    const hiddenInDatasetRowIds = new Set(
                      previewRows
                        .filter((r) => hiddenRowLabels.has(r.label))
                        .map((r) => r.id),
                    );
                    const hiddenInDatasetColumnIds = new Set(
                      previewColumns
                        .filter((c) => hiddenColumnLabels.has(c.label))
                        .map((c) => c.id),
                    );

                    return (
                      <CrosstabCustomizationPanel
                        rows={previewRows}
                        columns={previewColumns}
                        hiddenRows={currentConfig.hiddenRows}
                        hiddenColumns={currentConfig.hiddenColumns}
                        hiddenInDatasetRowIds={hiddenInDatasetRowIds}
                        hiddenInDatasetColumnIds={hiddenInDatasetColumnIds}
                        customRowLabels={currentConfig.customRowLabels}
                        customColumnLabels={currentConfig.customColumnLabels}
                        rowOrder={currentConfig.rowOrder}
                        columnOrder={currentConfig.columnOrder}
                        onToggleRowVisibility={toggleRowVisibility}
                        onToggleColumnVisibility={toggleColumnVisibility}
                        onUpdateRowLabel={updateCustomRowLabel}
                        onUpdateColumnLabel={updateCustomColumnLabel}
                        onResetRowLabel={resetCustomRowLabel}
                        onResetColumnLabel={resetCustomColumnLabel}
                        onShowAllRows={showAllRows}
                        onHideAllRows={hideAllRows}
                        onShowAllColumns={showAllColumns}
                        onHideAllColumns={hideAllColumns}
                        onReorderRows={reorderRows}
                        onReorderColumns={reorderColumns}
                        sortingOption={currentConfig.sortingOption}
                        onUpdateSortingOption={updateSortingOption}
                      />
                    );
                  })()}
              </Box>
            </Box>

            {/* Preview Panel */}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                p: 3,
              }}
            >
              <Typography variant="h6" sx={{ mb: 2 }}>
                Preview
              </Typography>
              <Box
                sx={{
                  flex: 1,
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  minHeight: 400,
                  p: 3,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "auto",
                  }}
                >
                  {!currentConfig.datasetId ? (
                    <EmptyChartState message="Please select a dataset from the chart creation modal" />
                  ) : !currentConfig.rowQuestion ? (
                    <EmptyChartState message="Select a row question to begin" />
                  ) : !currentConfig.columnQuestion ? (
                    <EmptyChartState message="Select a column question to continue" />
                  ) : rowNeedsFocus &&
                    !(currentConfig.rowFocus?.focusMetric === "rollup"
                      ? (currentConfig.rowFocus?.rollupName?.trim() && (currentConfig.rowFocus?.rollup?.sourceCategoryLabels?.length ?? 0) >= 2)
                      : (currentConfig.rowFocus?.selectedVariableId || currentConfig.rowFocus?.selectedValueId)) ? (
                    <EmptyChartState message="Select a focus type for the row question" />
                  ) : columnNeedsFocus &&
                    !(currentConfig.columnFocus?.focusMetric === "rollup"
                      ? (currentConfig.columnFocus?.rollupName?.trim() && (currentConfig.columnFocus?.rollup?.sourceCategoryLabels?.length ?? 0) >= 2)
                      : (currentConfig.columnFocus?.selectedVariableId || currentConfig.columnFocus?.selectedValueId)) ? (
                    <EmptyChartState message="Select a focus type for the column question" />
                  ) : isFetchingCrosstabData || isTogglingFilters ? (
                    <Box sx={{ textAlign: "center" }}>
                      <CircularProgress size={40} />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 2 }}
                      >
                        {isTogglingFilters
                          ? "Applying filters..."
                          : "Loading crosstab data..."}
                      </Typography>
                    </Box>
                  ) : isNoPeriodAvailable ? (
                    <EmptyChartState message={noPeriodMessage!} />
                  ) : crosstabPreviewData && crosstabPreviewData.length > 0 ? (
                    <Box sx={{ width: "100%", height: "100%" }}>
                      <CrosstabTable
                        data={crosstabPreviewData}
                        rowLabelColumn="label"
                        decimals={currentConfig.percentageFormatting}
                        hiddenRows={currentConfig.hiddenRows}
                        hiddenColumns={currentConfig.hiddenColumns}
                        customRowLabels={currentConfig.customRowLabels}
                        customColumnLabels={currentConfig.customColumnLabels}
                        rowOrder={currentConfig.rowOrder}
                        columnOrder={currentConfig.columnOrder}
                        showN={currentConfig.showN}
                        columnNValues={columnNValues}
                        hasSuppressedCells={hasSuppressedCells}
                        showPercentSign={
                          currentConfig.rowQuestion?.questionType ===
                            "Single" ||
                          currentConfig.rowQuestion?.questionType === "Multiple"
                        }
                        statisticalTesting={currentConfig.statisticalTesting}
                        timePeriodSelection={currentConfig.timePeriodSelection}
                        visualizationBase={currentConfig.visualizationBase}
                        datasetId={currentConfig.datasetId}
                        hasCustomTitle={
                          currentConfig.title !== "" &&
                          currentConfig.rowQuestion &&
                          currentConfig.columnQuestion &&
                          currentConfig.title !==
                            `Crosstab ${currentConfig.rowQuestion.questionTitle} by ${currentConfig.columnQuestion.questionTitle}`
                        }
                        weightOption={currentConfig.weightOption}
                        customWeight={currentConfig.customWeight}
                        rowCount={crosstabPreviewData.length}
                        rollingAverage={currentConfig.rollingAverage}
                        comparisonRollingLabel={
                          currentConfig.comparisonRollingLabel
                        }
                        rollingPeriodsWarningMessage={
                          currentConfig.rollingPeriodsWarningMessage
                        }
                        responsesMinDate={currentConfig.responsesMinDate}
                        effectiveRollingPeriodCount={
                          currentConfig.effectiveRollingPeriodCount
                        }
                        missingRollingPeriodLabels={
                          currentConfig.missingRollingPeriodLabels
                        }
                        dashboardDateOverride={dashboardDateOverride}
                        categoryRollups={currentConfig.categoryRollups}
                        variableIdLabelMap={currentConfig.variableIdLabelMap}
                        crossCategoryRollups={currentConfig.crossCategoryRollups}
                        crossVariableIdLabelMap={
                          currentConfig.crossVariableIdLabelMap
                        }
                        rowFocusRollupName={
                          currentConfig.rowFocus?.focusMetric === "rollup"
                            ? currentConfig.rowFocus?.rollupName
                            : undefined
                        }
                        colorCodingTemplateId={
                          currentConfig.colorCodingTemplateId
                        }
                        metricType={getMetricTypeFromQuestionType(
                          currentConfig.rowQuestion?.questionType,
                        )}
                      />
                    </Box>
                  ) : (
                    <EmptyChartState message="No data available for preview" />
                  )}
                </Box>
              </Box>
              <Box
                sx={{
                  mt: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                }}
              >
                {hasNoPreviousPeriodData && (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    No data available for the previous time period to perform
                    statistical testing.
                  </Alert>
                )}

                {crosstabPreviewData &&
                  crosstabPreviewData.length > 0 &&
                  currentConfig.rollingAverage?.enabled &&
                  currentConfig.rollingAverage?.windowSize !== undefined &&
                  currentConfig.effectiveRollingPeriodCount !== undefined &&
                  currentConfig.rollingAverage.windowSize >
                    currentConfig.effectiveRollingPeriodCount && (
                    <Alert severity="warning" sx={{ mb: 1 }}>
                      Warning: Insufficient historical data for selected rolling
                      window. Available periods:{" "}
                      {currentConfig.effectiveRollingPeriodCount}. Rolling
                      window reduced to{" "}
                      {currentConfig.effectiveRollingPeriodCount} periods.
                    </Alert>
                  )}

                {hasSampleSizeBelowThreshold && (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    {currentConfig.rollingAverage?.enabled
                      ? "Warning: Weighted base for one or more rolling windows is below the recommended threshold for reliable statistical testing."
                      : "Warning: Sample size for one or more time periods is below the recommended threshold for reliable statistical testing."}
                  </Alert>
                )}
              </Box>
            </Box>
          </Box>
          {crossRollupConfigError && (
            <Box sx={{ px: 2, pt: 1 }}>
              <Alert severity="error">{crossRollupConfigError}</Alert>
            </Box>
          )}
          <Box
            sx={{
              p: 2,
              borderTop: 1,
              borderColor: "divider",
              display: "flex",
              gap: 1,
              justifyContent: "flex-end",
            }}
          >
            <HPButton variant="text" onClick={handleDiscard}>
              Discard
            </HPButton>
            <HPButton
              variant="contained"
              onClick={handleSave}
              disabled={!canSave || isViewerOptionsInvalid || !!pitGuardrailConflict}
            >
              {editingWidgetId && editingWidgetId !== "new-widget"
                ? "Update"
                : "Save"}
            </HPButton>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

// Focus Selector Component - Extracted to avoid duplication
interface FocusSelectorProps {
  label: string;
  focusConfig: FocusSelection | null;
  variables: QuestionVariablesResponse | undefined;
  onFocusTypeChange: (focusType: "variable" | "value") => void;
  onVariableSelect: (variableId: string) => void;
  onValueSelect: (valueId: string) => void;
  disabled?: boolean;
  // Rollup support (optional — only passed for Row Focus)
  onFocusMetricChange?: (metric: "single" | "rollup") => void;
  onRollupNameChange?: (name: string) => void;
  onRollupLabelsChange?: (labels: string[]) => void;
  // Disables only the "Rollup" focus-metric option (e.g. under Per-Segment Weighting).
  rollupOptionDisabled?: boolean;
  rollupDisabledMessage?: string;
}

const FocusSelector: React.FC<FocusSelectorProps> = ({
  label,
  focusConfig,
  variables,
  onFocusTypeChange,
  onVariableSelect,
  onValueSelect,
  disabled = false,
  onFocusMetricChange,
  onRollupNameChange,
  onRollupLabelsChange,
  rollupOptionDisabled = false,
  rollupDisabledMessage,
}) => {
  const supportsRollup = !!onFocusMetricChange;
  const focusType = focusConfig?.focusType || "variable";
  const activeCount =
    focusType === "variable"
      ? (variables?.variables?.length ?? 0)
      : (variables?.values?.length ?? 0);
  const showMetricToggle = supportsRollup && activeCount >= 2;
  const focusMetric = focusConfig?.focusMetric ?? "single";
  const isRollup = supportsRollup && focusMetric === "rollup";

  return (
    <Box sx={{ mt: 2 }}>
      <FormLabel className="text-[12px] text-[#262626] mb-1 block">
        {label}
      </FormLabel>
      <RadioGroup
        value={focusType}
        onChange={(e) =>
          onFocusTypeChange(e.target.value as "variable" | "value")
        }
        sx={{ mb: 1 }}
      >
        <FormControlLabel
          value="variable"
          control={<Radio size="small" disabled={disabled} />}
          label="Focus on Variable"
          disabled={disabled}
        />
        <FormControlLabel
          value="value"
          control={<Radio size="small" disabled={disabled} />}
          label="Focus on Value"
          disabled={disabled}
        />
      </RadioGroup>

      {showMetricToggle && (
        <Box sx={{ mt: 1 }}>
          <FormLabel sx={{ fontSize: 12, color: "#262626", mb: 0.5, display: "block" }}>
            Focus Metric
          </FormLabel>
          <RadioGroup
            value={focusMetric}
            onChange={(e) => onFocusMetricChange!(e.target.value as "single" | "rollup")}
          >
            <FormControlLabel
              value="single"
              control={<Radio size="small" disabled={disabled} />}
              label="Single Category"
              disabled={disabled}
            />
            <FormControlLabel
              value="rollup"
              control={
                <Radio size="small" disabled={disabled || rollupOptionDisabled} />
              }
              label="Rollup"
              disabled={disabled || rollupOptionDisabled}
            />
          </RadioGroup>
          {rollupOptionDisabled && rollupDisabledMessage && (
            <Alert severity="info" sx={{ mt: 1, fontSize: "0.875rem" }}>
              {rollupDisabledMessage}
            </Alert>
          )}
        </Box>
      )}

      {isRollup ? (
        <Box sx={{ mt: 2 }}>
          <RollupBuilder
            focusMode={focusType}
            allVariables={variables?.variables ?? []}
            allValues={variables?.values ?? []}
            rollupName={focusConfig?.rollupName ?? ""}
            rollupSelectedLabels={focusConfig?.rollup?.sourceCategoryLabels ?? []}
            onRollupNameChange={onRollupNameChange!}
            onSelectedLabelsChange={onRollupLabelsChange!}
          />
        </Box>
      ) : (
        <>
          {focusType === "variable" && variables?.variables && (
            <FormControl fullWidth size="small" sx={{ mt: showMetricToggle ? 2 : 0 }}>
              <Select
                value={focusConfig?.selectedVariableId || ""}
                onChange={(e) => onVariableSelect(e.target.value)}
                displayEmpty
                disabled={disabled}
              >
                <MenuItem value="" disabled>
                  Select Variable
                </MenuItem>
                {filterVisiblePreservingSelected(
                  variables.variables,
                  (variable) =>
                    variable.datasetElementId ===
                    focusConfig?.selectedVariableId,
                ).map((variable) => (
                  <MenuItem
                    key={variable.datasetElementId}
                    value={variable.datasetElementId}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      {variable.title || variable.rowtitle || variable.coltitle}
                      {variable.hidden && (
                        <HiddenInDatasetBadge size="small" />
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {focusType === "value" && variables?.values && (
            <FormControl fullWidth size="small" sx={{ mt: showMetricToggle ? 2 : 0 }}>
              <Select
                value={focusConfig?.selectedValueId || ""}
                onChange={(e) => onValueSelect(e.target.value)}
                displayEmpty
                disabled={disabled}
              >
                <MenuItem value="" disabled>
                  Select Value
                </MenuItem>
                {filterVisiblePreservingSelected(
                  variables.values,
                  (value) =>
                    value.datasetElementId === focusConfig?.selectedValueId,
                ).map((value) => (
                  <MenuItem
                    key={value.datasetElementId}
                    value={value.datasetElementId}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      {value.title}
                      {value.hidden && <HiddenInDatasetBadge size="small" />}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </>
      )}
    </Box>
  );
};

export default CrosstabConfigDrawer;
